import {
  noticings,
  readPreferences,
  writePreferences,
  type Entry,
  type Noticing,
  type NoticingKind,
  type Tag,
  type Timestamp,
  type Track,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'

import { useServices } from '../../services/ServicesProvider.js'
import { trackFill } from '../../charts/colours.js'
import styles from './Noticings.module.css'

interface Props {
  readonly tracks: readonly Track[]
  readonly entries: readonly Entry[]
  readonly tags: readonly Tag[]
  readonly range: { readonly fromEpochMs: number; readonly toEpochMs: number }
  readonly now: Timestamp
}

/** Four or five, at most. */
const LIMIT = 5

/**
 * What the app noticed (T-1201).
 *
 * The first thing in this app that volunteers anything rather than answering a
 * question somebody thought to ask. Every sentence and every threshold is
 * core's; this renders a list and offers a way to hide a row.
 *
 * **It renders nothing at all when there is nothing to say**, which is most
 * days. That is not a degraded state to be filled with a placeholder — a
 * placeholder is how the section becomes furniture, and then the one observation
 * that mattered is furniture too.
 */
export function Noticings({ tracks, entries, tags, range, now }: Props) {
  const { storage } = useServices()
  const [hidden, setHidden] = useState<readonly string[] | null>(null)

  useEffect(() => {
    void (async () => {
      setHidden((await readPreferences(storage)).hiddenNoticings)
    })()
  }, [storage])

  const hide = useCallback(
    async (id: string) => {
      const preferences = await readPreferences(storage)
      const next = [...preferences.hiddenNoticings, id]
      setHidden(next)
      await writePreferences(storage, { ...preferences, hiddenNoticings: next })
    },
    [storage],
  )

  // `null` while unread, so a row never appears and then vanishes for somebody
  // who hid it weeks ago.
  if (hidden === null) {
    return null
  }

  const found = noticings(
    { tracks, entries, tags, range, now },
    { limit: LIMIT, dismissed: hidden },
  )
  if (found.length === 0) {
    return null
  }

  return (
    <section
      className={`${styles['section']} ${styles['noticing']}`}
      aria-labelledby="noticings-title"
      data-bleed
    >
      <h2 className={styles['eyebrow']} id="noticings-title">
        <span className={styles['eyebrowMark']} aria-hidden="true" />
        Worth noticing
      </h2>
      <ul className={styles['list']}>
        {found.map((one) => (
          <li key={one.id} className={styles['item']}>
            <span
              className={styles['mark']}
              style={{
                background:
                  one.track === null ? 'var(--border-strong)' : trackFill(one.track),
              }}
              aria-hidden="true"
            />
            <span className={styles['body']}>
              <span className={styles['headline']}>{one.headline}</span>
              <span className={styles['detail']}>{one.detail}</span>
              {/*
                What it was drawn from. An observation the reader cannot audit is
                one they either believe too much or ignore entirely, and "from 11
                readings" is the difference.
              */}
              <span className={styles['evidence']}>
                {kindLabel(one.kind)} · {one.evidence}
              </span>
            </span>
            <button
              type="button"
              className={styles['hide']}
              aria-label={`Hide: ${one.headline}`}
              onClick={() => void hide(one.id)}
            >
              <Cross />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * What kind of observation this is, in a word.
 *
 * Not a severity and not a score. A noticing is either worth showing or it is
 * not; labelling them by how alarming they sound is how a neutral list turns
 * into a league table of things to worry about.
 */
function kindLabel(kind: NoticingKind): string {
  switch (kind) {
    case 'run':
      return 'Recent readings'
    case 'timing':
      return 'When you record it'
    case 'weekday':
      return 'Day of the week'
    case 'tag':
      return 'Alongside a tag'
    case 'return':
      return 'A gap in the range'
  }
}

/** Exported for the test, which asserts every kind has a label. */
export const NOTICING_KIND_LABEL = kindLabel

function Cross() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M7 7l10 10M17 7L7 17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
      />
    </svg>
  )
}

/** Re-exported so the screen can size its own empty state around this one. */
export type { Noticing }
